/*
 * Khai báo thông tin ban đầu — khách tự điền trên iPad / kiosk ngay sau check-in, theo mẫu phiếu giấy Khơ Thị đang dùng:
 * thông tin cá nhân, bệnh sử, thực phẩm chức năng, thuốc, tình trạng cơ thể, thông tin thăm viện, người viết đơn + ký tay.
 * Mở từ kiosk (?appt = mã lịch hẹn trên hệ thống) hoặc từ App Tư vấn / Lễ tân (?v = mã lượt).
 * Có lịch hẹn thì lưu lên server (POST, sửa bằng PUT); lượt không có lịch hẹn và khách mẫu vẫn lưu trên máy.
 * Kiosk đặt ở sảnh: không đọc phiếu cũ, không điền sẵn dữ liệu sức khoẻ.
 */
import React, { useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { Input, InputNumber, Select, message } from 'antd';
import { Check } from 'lucide-react';
import { SCR, emptyScreening, screeningIssues, type Screening } from './logic';
import { maskName, maskPhone } from '../CheckIn';
import { currentStaffName } from '../Telesales/lead/store';
import { createDeclaration, forgetDeclarations, getDeclaration, toAnswers, toScreening, updateDeclaration, useReceptionStaffs, type ReceptionStaff } from './declarationApi';
import { useScreening } from './shared';
import { useTuVan } from './store';
import SignaturePad from './SignaturePad';

/** Kiosk: không chạm màn hình chừng này thì bỏ form, về màn chờ */
const IDLE_MS = 90_000;

/* ---------------- Ô chọn cỡ lớn cho màn cảm ứng ---------------- */

/** Chọn một nhân sự theo danh sách trên server; giá trị lưu là staffs.id */
function StaffChips({ list, value, onChange }: { list?: ReceptionStaff[] | null; value?: string; onChange: (id?: string) => void }) {
  if (list === undefined) return <div className="text-[13px] text-gray-500">Đang tải danh sách…</div>;
  if (list === null) return <div className="text-[13px] text-gray-500">Chưa tải được danh sách, có thể bỏ qua mục này.</div>;
  // Danh sách dài (tư vấn viên toàn hệ thống) thì tìm theo tên, không dàn hết ra màn hình
  if (list.length > 12) {
    return (
      <Select
        size="large" showSearch allowClear className="w-full" placeholder="Gõ để tìm tên" optionFilterProp="label"
        value={list.some((x) => String(x.id) === String(value)) ? String(value) : undefined}
        options={list.map((x) => ({ value: String(x.id), label: x.name }))}
        onChange={(v) => onChange(v)}
      />
    );
  }
  const name = list.find((x) => String(x.id) === String(value))?.name;
  return <Chips single options={list.map((x) => x.name)} value={name ? [name] : []} onChange={(x) => { const hit = list.find((y) => y.name === x[0]); onChange(hit ? String(hit.id) : undefined); }} />;
}

function Chips({ options, value, onChange, single }: { options: string[]; value: string[]; onChange: (x: string[]) => void; single?: boolean }) {
  return (
    <div className="flex flex-wrap gap-2">
      {options.map((o) => {
        const on = value.includes(o);
        return (
          <button key={o} type="button" aria-pressed={on}
            onClick={() => onChange(single ? (on ? [] : [o]) : on ? value.filter((x) => x !== o) : [...value, o])}
            className={`flex items-center gap-1.5 rounded-xl border px-3.5 py-2.5 text-[14px] transition ${on
              ? 'border-[#092C23] bg-[#092C23] text-white'
              : 'border-gray-300 bg-white text-gray-700 hover:border-[#C79F4B]'}`}>
            <span className={`flex h-4 w-4 items-center justify-center rounded border ${on ? 'border-white bg-white text-[#092C23]' : 'border-gray-400'}`}>{on && <Check size={12} strokeWidth={3} />}</span>
            {o}
          </button>
        );
      })}
    </div>
  );
}
const YesNo = ({ value, onChange }: { value?: boolean; onChange: (x: boolean) => void }) => (
  <Chips single options={['Có', 'Không']} value={value == null ? [] : [value ? 'Có' : 'Không']} onChange={(x) => onChange(x[0] === 'Có')} />
);

const Section = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <section className="rounded-2xl border border-gray-200 bg-white p-4 sm:p-5">
    <h2 className="mb-4 text-[15px] font-bold uppercase tracking-wide text-[#092C23]">{title}</h2>
    <div className="space-y-4">{children}</div>
  </section>
);
const Field = ({ label, req, children }: { label: string; req?: boolean; children: React.ReactNode }) => (
  <div>
    <div className="mb-1.5 text-[13.5px] font-semibold text-gray-700">{label}{req && <span className="text-red-500"> *</span>}</div>
    {children}
  </div>
);

/* ---------------- Trang ---------------- */

export default function KhaiBao() {
  const [sp] = useSearchParams();
  const navigate = useNavigate();
  const loc = useLocation();
  const st = (loc.state ?? {}) as { name?: string; phone?: string; address?: string; back?: string };
  const apptId = sp.get('appt') ?? undefined;
  const visitId = sp.get('v') ?? undefined;
  const { visits, update } = useTuVan();
  const v = visits.find((x) => (visitId ? x.id === visitId : apptId != null && String(x.apiApptId) === apptId));
  const fromKiosk = !visitId;
  // Kiosk không đọc phiếu cũ; mở từ lượt thì nạp phiếu đã khai để sửa
  const sc = useScreening(fromKiosk ? undefined : v);
  const prev = sc.s;
  const [declId, setDeclId] = useState<number>();
  const editing = declId != null;
  const [sending, setSending] = useState(false);
  const doctors = useReceptionStaffs('doctor');
  const consultants = useReceptionStaffs('consultant');
  const [f, setF] = useState<Screening>(() => prev ?? {
    ...emptyScreening(),
    name: st.name ?? v?.cust.name,
    phone: st.phone ?? v?.phoneDigits,
    address: st.address || undefined,
    gender: v?.cust.gender,
  });
  const [done, setDone] = useState(false);
  // Người viết là người bảo hộ: cờ riêng, không suy từ ô "Quan hệ" (xoá chữ trong ô đó để gõ lại không được làm mất các ô)
  const [guardian, setGuardian] = useState(() => !!prev?.writerRelation);
  const set = (p: Partial<Screening>) => setF((x) => ({ ...x, ...p }));
  // Kiosk: tên, số, địa chỉ điền sẵn từ check-in được che như màn xác nhận; chạm vào thì nhập lại, bỏ trống thì giữ như cũ
  const seed = useRef({ name: f.name, phone: f.phone, address: f.address });
  const [masked, setMasked] = useState({ name: fromKiosk && !!f.name, phone: fromKiosk && !!f.phone, address: fromKiosk && !!f.address });
  const unmask = (k: 'name' | 'phone' | 'address') => { if (masked[k]) { setMasked((x) => ({ ...x, [k]: false })); set({ [k]: '' }); } };
  const remask = (k: 'name' | 'phone' | 'address') => { if (fromKiosk && seed.current[k] && !f[k]?.trim()) { setMasked((x) => ({ ...x, [k]: true })); set({ [k]: seed.current[k] }); } };
  const openDecl = (s: Screening, id?: number) => { setF(s); setGuardian(!!s.writerRelation); setDeclId(id); };
  // Phiếu trên server tải xong sau khi form đã dựng: nạp một lần, không đè chữ đang gõ
  const loaded = useRef(false);
  useEffect(() => {
    if (loaded.current || sc.loading || !sc.declId || !sc.s) return;
    loaded.current = true;
    openDecl(sc.s, sc.declId);
  }, [sc.loading, sc.declId]); // eslint-disable-line react-hooks/exhaustive-deps
  const back = st.back ?? (v ? `/phieu-tu-van?v=${v.id}` : '/check-in');

  // Xong thì sau vài giây tự về (kiosk về màn chờ cho khách sau)
  useEffect(() => {
    if (!done) return undefined;
    const t = setTimeout(() => navigate(back), 6000);
    return () => clearTimeout(t);
  }, [done, back, navigate]);

  // Mở từ kiosk mà khách bỏ đi không điền: không chạm màn hình 90 giây thì về màn chờ,
  // để khách sau không thấy tên / SĐT điền sẵn của khách trước và không phải bấm "Bỏ qua"
  useEffect(() => {
    if (!fromKiosk || done) return undefined;
    let t = setTimeout(() => navigate(back), IDLE_MS);
    const poke = () => { clearTimeout(t); t = setTimeout(() => navigate(back), IDLE_MS); };
    const evs = ['pointerdown', 'keydown', 'input', 'scroll'] as const;
    evs.forEach((e) => window.addEventListener(e, poke, { passive: true, capture: true }));
    return () => { clearTimeout(t); evs.forEach((e) => window.removeEventListener(e, poke, { capture: true })); };
  }, [fromKiosk, done, back, navigate]);

  const missing = [
    !f.name?.trim() && 'Họ tên',
    f.gender == null && 'Giới tính',
    !f.phone?.trim() && 'Số điện thoại',
    f.channels.includes('Người quen giới thiệu') && !f.referrer?.trim() && 'Tên người giới thiệu',
    // Sửa phiếu giữ chữ ký gốc, không đòi ký lại
    !editing && !f.signature && 'Chữ ký',
    !editing && !f.consent && 'Đồng ý lưu thông tin',
  ].filter(Boolean) as string[];

  // Kiosk là khách tự khai; mở từ lượt là nhân viên thao tác
  const by = fromKiosk ? (f.writerName || f.name || 'Khách') : currentStaffName();
  const logText = editing ? 'Nhân viên sửa khai báo' : fromKiosk ? 'Khách khai báo thông tin ban đầu' : 'Nhân viên nhập khai báo giúp khách';
  const submit = async () => {
    if (sending) return;
    if (missing.length) { message.warning(`Vui lòng điền: ${missing.join(', ')}`); return; }
    const issues = screeningIssues(f);
    if (issues.length) { message.warning(`Vui lòng kiểm tra lại: ${issues.join(', ')}`); return; }
    const appt = apptId != null ? Number(apptId) : v?.apiApptId;
    // Khách vãng lai không có lịch: gắn phiếu theo lượt trên server
    const key = appt != null ? { appt } : { visit: v?.serverId };
    // Lượt chỉ có trên máy: chưa có gì để gắn phiếu trên server
    if (appt == null && v?.serverId == null) {
      if (v) update(v.id, () => ({ screening: { ...f, at: Date.now() } }), { text: logText, by });
      setDone(true);
      return;
    }
    setSending(true);
    const res = editing
      ? await updateDeclaration(declId, toAnswers(f))
      : await createDeclaration({ ...(appt != null ? { appointment_id: appt } : { visit_id: v?.serverId }), source: fromKiosk ? 'kiosk' : 'staff', answers: toAnswers(f), signature: f.signature!, consent: true });
    if (res.ok || (res.code === 'DECLARATION_EXISTS' && fromKiosk)) {
      setSending(false);
      forgetDeclarations(key);
      if (v) update(v.id, () => ({ screening: undefined }), { text: logText, by });
      if (editing) { message.success('Đã lưu khai báo'); navigate(back); } else setDone(true);
      return;
    }
    if (res.code === 'DECLARATION_EXISTS') {
      // Lượt này đã khai: nhân viên mở phiếu đó để sửa, không tạo thêm
      const id = res.body?.id ?? res.body?.data?.id ?? res.body?.errors?.id;
      const old = id ? await getDeclaration(id) : undefined;
      setSending(false);
      if (old?.ok && old.data) { openDecl(toScreening(old.data), old.data.id); message.warning('Lượt này đã có phiếu khai báo. Nội dung vừa nhập chưa được lưu, đã mở phiếu đang có để sửa.', 6); }
      else message.error('Lượt này đã có phiếu khai báo nhưng chưa mở được phiếu.');
      return;
    }
    setSending(false);
    // Lỗi mạng, lỗi máy chủ: giữ nguyên form để gửi lại
    if (res.code === 'NOT_CHECKED_IN') message.error('Khách chưa check-in hôm nay, vui lòng liên hệ lễ tân.');
    else message.error(res.message || 'Chưa gửi được, vui lòng thử lại.');
  };

  if (done) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-[#092C23] px-6 text-center text-white">
        <Helmet><title>Khai báo thông tin | Khothi</title></Helmet>
        <div className="flex h-16 w-16 items-center justify-center rounded-full border-2 border-[#C79F4B] text-[#C79F4B]"><Check size={32} /></div>
        <h1 className="text-2xl font-bold">Cảm ơn quý khách</h1>
        <p className="max-w-md text-white/80">Thông tin đã được gửi tới tư vấn viên và bác sĩ. Quý khách vui lòng ngồi chờ, nhân viên sẽ mời vào phòng tư vấn.</p>
        <button type="button" onClick={() => navigate(back)} className="mt-2 rounded-xl bg-[#C79F4B] px-6 py-3 font-semibold">Hoàn tất</button>
      </div>
    );
  }

  if (!fromKiosk && (sc.loading || (sc.error && !sc.s))) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-3 bg-[#f4f1ea] px-6 text-center">
        <Helmet><title>Khai báo thông tin | Khothi</title></Helmet>
        <p className="text-gray-700">{sc.loading ? 'Đang tải phiếu khai báo…' : `${sc.error}. Tải lại trang để thử lại.`}</p>
        {!sc.loading && <button type="button" onClick={() => navigate(back)} className="rounded-xl border border-gray-300 px-4 py-2 text-[14px] text-gray-600">Quay lại</button>}
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f4f1ea] pb-28">
      <Helmet><title>Khai báo thông tin | Khothi</title></Helmet>
      <header className="bg-[#092C23] px-5 py-5 text-center text-white">
        <div className="text-[11px] font-semibold uppercase tracking-[0.2em] text-[#C79F4B]">Khơ Thị · Trần Hưng Đạo</div>
        <h1 className="mt-1 text-xl font-bold sm:text-2xl">Đăng ký thông tin khách hàng</h1>
        <p className="mx-auto mt-1 max-w-xl text-[13px] text-white/75">Để việc tư vấn và làm dịch vụ được an toàn, xin quý khách tích hoặc điền các thông tin dưới đây.</p>
      </header>

      <main className="mx-auto max-w-3xl space-y-4 px-4 pt-4">
        <Section title="Thông tin cá nhân">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label="Họ và tên" req><Input size="large" value={masked.name ? maskName(f.name) : f.name} onFocus={() => unmask('name')} onBlur={() => remask('name')} onChange={(e) => set({ name: e.target.value })} placeholder="Nguyễn Thị A" /></Field>
            <Field label="Ngày sinh"><Input size="large" value={f.dob} onChange={(e) => set({ dob: e.target.value })} placeholder="dd/mm/yyyy" inputMode="numeric" /></Field>
            <Field label="Giới tính" req><Chips single options={['Nữ', 'Nam']} value={f.gender === 1 ? ['Nam'] : f.gender === 2 ? ['Nữ'] : []} onChange={(x) => set({ gender: x[0] ? (x[0] === 'Nam' ? 1 : 2) : undefined })} /></Field>
            <Field label="Tuổi"><InputNumber size="large" min={1} max={120} value={f.age} onChange={(x) => set({ age: x ?? undefined })} className="!w-full" /></Field>
            <Field label="Số điện thoại" req><Input size="large" value={masked.phone ? maskPhone(f.phone) : f.phone} onFocus={() => unmask('phone')} onBlur={() => remask('phone')} onChange={(e) => set({ phone: e.target.value })} inputMode="tel" /></Field>
            <Field label="Số điện thoại dự phòng"><Input size="large" value={f.phone2} onChange={(e) => set({ phone2: e.target.value })} inputMode="tel" /></Field>
            <Field label="E-mail"><Input size="large" value={f.email} onChange={(e) => set({ email: e.target.value })} inputMode="email" /></Field>
            <Field label="Địa chỉ"><Input size="large" value={masked.address ? maskName(f.address) : f.address} onFocus={() => unmask('address')} onBlur={() => remask('address')} onChange={(e) => set({ address: e.target.value })} /></Field>
          </div>
          <Field label="Nhóm máu">
            <Chips single options={SCR.blood} value={f.blood ? [f.blood] : []} onChange={(x) => set({ blood: x[0] })} />
            {f.blood === 'Loại khác' && <Input className="mt-2" value={f.bloodOther} onChange={(e) => set({ bloodOther: e.target.value })} placeholder="Nhóm máu khác" />}
          </Field>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label="Tình trạng hôn nhân"><Chips single options={['Chưa kết hôn', 'Đã kết hôn']} value={f.married == null ? [] : [f.married ? 'Đã kết hôn' : 'Chưa kết hôn']} onChange={(x) => set({ married: x[0] ? x[0] === 'Đã kết hôn' : undefined })} /></Field>
            <Field label="Con cái"><Input size="large" value={f.children} onChange={(e) => set({ children: e.target.value })} placeholder="VD: 2 con" /></Field>
          </div>
          <Field label="Nghề nghiệp"><Chips single options={SCR.jobs} value={f.job ? [f.job] : []} onChange={(x) => set({ job: x[0] })} /></Field>
        </Section>

        <Section title="Thông tin sức khoẻ">
          <Field label="Bệnh sử trong quá khứ và hiện tại">
            <Chips options={SCR.history} value={f.history} onChange={(x) => set({ history: x })} />
            {f.history.includes('Khác') && <Input className="mt-2" value={f.historyOther} onChange={(e) => set({ historyOther: e.target.value })} placeholder="Bệnh khác" />}
          </Field>
          <Field label="Phương pháp trị liệu đã nhận của bệnh đã chọn (VD: tiểu phẫu, uống thuốc…)">
            <Input.TextArea rows={2} value={f.treatment} onChange={(e) => set({ treatment: e.target.value })} />
          </Field>
          <Field label="Có bệnh sử đã phẫu thuật?">
            <YesNo value={f.surgery} onChange={(x) => set({ surgery: x })} />
            {f.surgery && <Input className="mt-2" value={f.surgeryNote} onChange={(e) => set({ surgeryNote: e.target.value })} placeholder="Phẫu thuật gì, khi nào" />}
          </Field>
          <Field label="Thực phẩm chức năng đang dùng"><Chips options={SCR.supplements} value={f.supplements} onChange={(x) => set({ supplements: x })} /></Field>
          <Field label="Loại thuốc đang dùng">
            <Chips options={SCR.meds} value={f.meds} onChange={(x) => set({ meds: x })} />
            {f.meds.includes('Khác') && <Input className="mt-2" value={f.medsOther} onChange={(e) => set({ medsOther: e.target.value })} placeholder="Thuốc khác" />}
          </Field>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label="Bình thường dễ bị sưng, bầm tím?"><YesNo value={f.bruise} onChange={(x) => set({ bruise: x })} /></Field>
            <Field label="Hút thuốc"><YesNo value={f.smoke} onChange={(x) => set({ smoke: x })} /></Field>
          </div>
        </Section>

        <Section title="Thông tin thăm viện">
          <Field label="Biết đến Khơ Thị qua">
            <Chips options={SCR.channels} value={f.channels} onChange={(x) => set({ channels: x })} />
            {f.channels.some((c) => c.includes('giới thiệu')) && (
              <Input className="mt-2" value={f.referrer} onChange={(e) => set({ referrer: e.target.value })} placeholder="Tên người giới thiệu" />
            )}
          </Field>
          <Field label="Bác sĩ chỉ định (nếu có)">
            <StaffChips list={doctors} value={f.doctor} onChange={(doctor) => set({ doctor })} />
          </Field>
          <Field label="Tư vấn viên chỉ định (nếu có)">
            <StaffChips list={consultants} value={f.consultant} onChange={(consultant) => set({ consultant })} />
          </Field>
          <Field label="Muốn được tư vấn về">
            <Chips options={SCR.interests} value={f.interests} onChange={(x) => set({ interests: x })} />
            {f.interests.includes('Khác') && <Input className="mt-2" value={f.interestOther} onChange={(e) => set({ interestOther: e.target.value })} placeholder="Nội dung khác" />}
          </Field>
        </Section>

        <Section title="Người viết đơn">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label="Người viết"><Chips single options={['Bệnh nhân', 'Người bảo hộ']} value={[guardian ? 'Người bảo hộ' : 'Bệnh nhân']} onChange={(x) => {
              const g = x[0] === 'Người bảo hộ';
              setGuardian(g);
              set(g ? { writerRelation: f.writerRelation || 'Người thân' } : { writerRelation: undefined, writerName: undefined });
            }} /></Field>
            {guardian && <Field label="Quan hệ với bệnh nhân"><Input size="large" value={f.writerRelation} onChange={(e) => set({ writerRelation: e.target.value })} placeholder="VD: Mẹ, chồng" /></Field>}
            {guardian && <Field label="Tên người bảo hộ"><Input size="large" value={f.writerName} onChange={(e) => set({ writerName: e.target.value })} /></Field>}
          </div>
          {editing
            ? <Field label="Chữ ký">{f.signature && <img src={f.signature} alt="Chữ ký khách" className="h-16 rounded border border-gray-200 bg-white" />}<div className="mt-1 text-[12.5px] text-gray-500">Giữ chữ ký gốc khi sửa phiếu.</div></Field>
            : <Field label="Chữ ký" req><SignaturePad value={f.signature} onChange={(x) => set({ signature: x })} /></Field>}
          {!editing && (
          <label className="flex items-start gap-3 rounded-xl bg-gray-50 p-3 text-[13.5px] text-gray-700">
            <input type="checkbox" className="mt-0.5 h-5 w-5 accent-[#092C23]" checked={!!f.consent} onChange={(e) => set({ consent: e.target.checked })} />
            <span>Tôi đồng ý cho Khơ Thị lưu các thông tin trên để hỗ trợ quá trình điều trị, và nhận thông tin dịch vụ qua tin nhắn, điện thoại, email. <span className="text-red-500">*</span></span>
          </label>
          )}
        </Section>
      </main>

      <footer className="fixed inset-x-0 bottom-0 border-t border-gray-200 bg-white/95 px-4 py-3 backdrop-blur">
        <div className="mx-auto flex max-w-3xl items-center gap-3">
          <button type="button" onClick={() => navigate(back)} className="rounded-xl border border-gray-300 px-4 py-3 text-[14px] text-gray-600">Bỏ qua</button>
          <button type="button" disabled={sending} onClick={submit} className={`flex-1 rounded-xl py-3 text-[15px] font-semibold text-white ${missing.length || sending ? 'bg-[#092C23]/50' : 'bg-[#092C23]'}`}>
            {sending ? 'Đang gửi…' : editing ? 'Lưu thay đổi' : 'Gửi thông tin'}{missing.length && !sending ? ` · còn thiếu ${missing.length} mục` : ''}
          </button>
        </div>
      </footer>
    </div>
  );
}
